import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  Tag,
  Badge,
  message,
  Modal,
  Input,
  Form,
  Select,
  Spin,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  UserDeleteOutlined,
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../../../../../config/axiosConfig';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ActiveCustomersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeCustomers, setActiveCustomers] = useState([]);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [actionType, setActionType] = useState(''); // 'deactivate' or 'block'
  const [actionForm] = Form.useForm();

  // Mock data - replace with API call
  const mockActiveCustomers = [
    {
      id: 1,
      customerName: 'TechCorp Solutions',
      businessType: 'Technology',
      joinDate: '2024-01-15',
      planType: 'Enterprise',
      status: 'active',
      lastActivity: '2025-01-10',
      messagesThisMonth: 15420,
      email: 'contact@techcorp.com',
      phone: '+1-234-567-8900',
      revenue: 2500.00
    },
    {
      id: 2,
      customerName: 'Digital Marketing Pro',
      businessType: 'Marketing',
      joinDate: '2024-02-20',
      planType: 'Premium',
      status: 'active',
      lastActivity: '2025-01-09',
      messagesThisMonth: 8900,
      email: 'info@digitalmarketing.com',
      phone: '+1-234-567-8901',
      revenue: 1200.00
    },
    {
      id: 3,
      customerName: 'E-commerce Hub',
      businessType: 'Retail',
      joinDate: '2024-03-10',
      planType: 'Business',
      status: 'active',
      lastActivity: '2025-01-11',
      messagesThisMonth: 12300,
      email: 'admin@ecommercehub.com',
      phone: '+1-234-567-8902',
      revenue: 1800.00
    },
    {
      id: 4,
      customerName: 'Healthcare Connect',
      businessType: 'Healthcare',
      joinDate: '2024-01-05',
      planType: 'Enterprise',
      status: 'active',
      lastActivity: '2025-01-08',
      messagesThisMonth: 22100,
      email: 'support@healthcareconnect.com',
      phone: '+1-234-567-8903',
      revenue: 3200.00
    },
    {
      id: 5,
      customerName: 'StartupXYZ',
      businessType: 'Technology',
      joinDate: '2024-06-15',
      planType: 'Basic',
      status: 'active',
      lastActivity: '2025-01-07',
      messagesThisMonth: 2400,
      email: 'hello@startupxyz.com',
      phone: '+1-234-567-8904',
      revenue: 500.00
    }
  ];

  useEffect(() => {
    fetchActiveCustomers();
  }, []);

  const fetchActiveCustomers = async () => {
    setLoading(true);
    try {
      const requestBody = {
        companystatus: "active",
        page: 1,
        limit: 100
      };

      const response = await axiosConfig.post('/company/getcompanydetails', requestBody);
      
      if (response.data?.status === true && response.data?.statuscode === 200) {
        const companies = response.data.entity.companies || [];
        // Transform API data to match component expectations
        const transformedCustomers = companies.map((company, index) => ({
          id: company.companyid,
          customerName: company.companyname,
          businessType: company.company_type === 'SOLUTION_PARTNER' ? 'Solution Partner' : 'Direct Customer',
          joinDate: company.entrytime,
          planType: company.company_type === 'SOLUTION_PARTNER' ? 'Partner' : 'Business',
          status: 'active',
          lastActivity: company.updatetime,
          messagesThisMonth: (company.activecustomer || 0) * 150,
          email: company.emailid,
          phone: `${company.countrycode || ''}${company.contactno || ''}`,
          revenue: (company.activecustomer || 0) * 25
        }));
        setActiveCustomers(transformedCustomers);
      } else {
        // Fallback to mock data if API fails
        setActiveCustomers(mockActiveCustomers);
      }
    } catch (error) {
      console.error('Error fetching active customers:', error);
      message.error('Failed to load active customers');
      // Fallback to mock data
      setActiveCustomers(mockActiveCustomers);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerAction = async (values) => {
    try {
      setLoading(true);
      // API call to deactivate or block customer
      // await axiosConfig.post(`/admin/${actionType}-customer/${selectedCustomer.id}`, values);
      
      message.success(`Customer ${actionType}d successfully`);
      setActiveCustomers(prev => prev.filter(item => item.id !== selectedCustomer.id));
      setActionModalVisible(false);
      setSelectedCustomer(null);
      setActionType('');
      actionForm.resetFields();
    } catch (error) {
      message.error(`Failed to ${actionType} customer`);
    } finally {
      setLoading(false);
    }
  };

  const showActionModal = (customer, type) => {
    setSelectedCustomer(customer);
    setActionType(type);
    setActionModalVisible(true);
  };

  const columns = [
    {
      title: 'Sr No',
      key: 'srNo',
      width: 80,
      render: (_, record, index) => index + 1
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <Space direction="vertical" size="small">
          <Text strong style={{ fontSize: '14px' }}>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.businessType} • {record.planType}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.email}
          </Text>
        </Space>
      )
    },
    {
      title: 'Join Date',
      dataIndex: 'joinDate',
      key: 'joinDate',
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.joinDate).unix() - moment(b.joinDate).unix()
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Active
        </Tag>
      )
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date) => (
        <Space direction="vertical" size="small">
          <Text style={{ fontSize: '12px' }}>
            {moment(date).format('MMM DD, YYYY')}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {moment(date).fromNow()}
          </Text>
        </Space>
      ),
      sorter: (a, b) => moment(a.lastActivity).unix() - moment(b.lastActivity).unix()
    },
    {
      title: 'Messages This Month',
      dataIndex: 'messagesThisMonth',
      key: 'messagesThisMonth',
      render: (count) => (
        <Text strong style={{ color: '#1890ff' }}>
          {count.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.messagesThisMonth - b.messagesThisMonth
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${amount.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.revenue - b.revenue
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '180px' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Tooltip title="Temporarily deactivate customer">
              <Button 
                size="small" 
                icon={<StopOutlined />}
                onClick={() => showActionModal(record, 'deactivate')}
                style={{ 
                  flex: 1,
                  borderColor: '#fa8c16',
                  color: '#fa8c16',
                  fontWeight: 500
                }}
              >
                Deactivate
              </Button>
            </Tooltip>
            <Tooltip title="Permanently block customer">
              <Button 
                danger
                size="small" 
                icon={<LockOutlined />}
                onClick={() => showActionModal(record, 'block')}
                style={{ 
                  flex: 1,
                  fontWeight: 500
                }}
              >
                Block
              </Button>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              style={{ 
                flex: 1,
                borderColor: '#722ed1',
                color: '#722ed1',
                fontWeight: 500
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      )
    }
  ];

  return (
    <div>
      <Spin spinning={loading}>
        {/* Header */}
        <Card 
          bordered={false} 
          bodyStyle={{ padding: '20px 24px' }}
          style={{ marginBottom: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard/admin')}
                type="default"
                size="large"
                style={{ 
                  fontWeight: 500
                }}
              >
                Back
              </Button>
              <div>
                <Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                  Active Customers
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  Manage active customer accounts and permissions
                </Text>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Badge 
                count={activeCustomers.length} 
                style={{ 
                  backgroundColor: '#52c41a',
                  boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                }} 
                showZero
              />
              <Button 
                type="default" 
                icon={<ReloadOutlined />} 
                onClick={fetchActiveCustomers}
                size="large"
                style={{
                  fontWeight: 500,
                   borderColor: '#52c41a',
                  color: '#52c41a'
                }}
              >
               
              </Button>
              <Button 
                type="default" 
                onClick={() => navigate('/dashboard/admin/pending-customers')}
                size="large"
                style={{
                  fontWeight: 500,
                  borderColor: '#fa8c16',
                  color: '#fa8c16'
                }}
              >
                View Pending
              </Button>
            </div>
          </div>
        </Card>

        {/* Active Customers Table */}
        <Card 
          bordered={false}
          bodyStyle={{ padding: '20px' }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '16px' }}>Active Customer Accounts</Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {activeCustomers.length} active customers
              </Text>
            </div>
          }
        >
          <Table
            dataSource={activeCustomers}
            columns={columns}
            rowKey="id"
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} active customers`
            }}
          />
        </Card>

        {/* Action Modal */}
        <Modal
          title={`${actionType === 'deactivate' ? 'Deactivate' : 'Block'} Customer`}
          open={actionModalVisible}
          onCancel={() => {
            setActionModalVisible(false);
            setSelectedCustomer(null);
            setActionType('');
            actionForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={actionForm}
            layout="vertical"
            onFinish={handleCustomerAction}
          >
            <Form.Item
              name="reason"
              label={`${actionType === 'deactivate' ? 'Deactivation' : 'Block'} Reason`}
              rules={[{ required: true, message: 'Please select a reason' }]}
            >
              <Select placeholder={`Select ${actionType} reason`}>
                <Option value="policy_violation">Policy Violation</Option>
                <Option value="payment_issues">Payment Issues</Option>
                <Option value="suspicious_activity">Suspicious Activity</Option>
                <Option value="customer_request">Customer Request</Option>
                <Option value="maintenance">Maintenance</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="comments"
              label="Additional Comments"
              rules={[{ required: true, message: 'Please provide additional comments' }]}
            >
              <TextArea 
                rows={4} 
                placeholder={`Provide detailed explanation for ${actionType}...`}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  danger={actionType === 'block'}
                  style={{
                    backgroundColor: actionType === 'deactivate' ? '#fa8c16' : undefined,
                    borderColor: actionType === 'deactivate' ? '#fa8c16' : undefined
                  }}
                >
                  {actionType === 'deactivate' ? 'Deactivate Customer' : 'Block Customer'}
                </Button>
                <Button onClick={() => {
                  setActionModalVisible(false);
                  setSelectedCustomer(null);
                  setActionType('');
                  actionForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </div>
  );
};

export default ActiveCustomersPage;
